import { useEffect, useRef, useState } from "react";
import { Facebook, ExternalLink, RefreshCw } from "lucide-react";
import { useContent } from "@/context/ContentContext";

/**
 * FacebookFeed — Embedded Facebook Page Plugin strip.
 *
 * Renders a compact strip on the homepage that pulls the latest posts from
 * Hondo's Facebook page directly (no API key required — uses the public FB
 * Page Plugin). Only renders when `content.contact.facebook_url` is set and
 * looks like a valid facebook.com URL.
 */
export default function FacebookFeed() {
  const { content } = useContent();
  const facebookUrl = (content?.contact?.facebook_url || "").trim();
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  const looksLikeFacebook = /^https?:\/\/(www\.)?(facebook|fb)\.com\//i.test(facebookUrl);

  useEffect(() => {
    if (!looksLikeFacebook) return;

    // Inject the SDK once.
    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0";
      script.onerror = () => setFailed(true);
      document.body.appendChild(script);
    }

    // Give the SDK a moment then re-parse this container.
    const parse = () => {
      if (window.FB && containerRef.current) {
        window.FB.XFBML.parse(containerRef.current);
        setReady(true);
      }
    };
    const t1 = setTimeout(parse, 400);
    const t2 = setTimeout(parse, 1500);
    const t3 = setTimeout(() => {
      // If FB never loaded (adblock / privacy blocker), show fallback.
      if (!window.FB) setFailed(true);
    }, 4500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [facebookUrl, looksLikeFacebook]);

  if (!looksLikeFacebook) return null;

  return (
    <section
      id="facebook-feed"
      data-testid="facebook-feed-section"
      className="relative py-20 md:py-28 bg-white border-y border-[#E5E7EB] overflow-hidden"
    >
      {/* diagonal industrial stripe accent */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-1/3 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #84FB00 0px, #84FB00 2px, transparent 2px, transparent 14px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Left rail — headline & CTA */}
          <div className="md:col-span-5 lg:col-span-4">
            <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">
              LIVE — FROM THE JOBSITE
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
              Straight From<br />
              <span className="text-[#9CA3AF]">The Feed</span>
            </h2>
            <p className="mt-6 text-[#374151] leading-relaxed max-w-md">
              Fresh builds, weld work and shop updates — pulled directly from our
              Facebook page. What you see here is what we're working on right now.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid="facebook-feed-visit"
                href={facebookUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0E63D4] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm transition-colors"
              >
                <Facebook size={16} /> Follow on Facebook
              </a>
              <a
                data-testid="facebook-feed-open"
                href={facebookUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 border border-[#D1D5DB] hover:border-[#84FB00] hover:text-[#4AB300] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm transition-colors"
              >
                <ExternalLink size={14} /> Open Page
              </a>
            </div>
          </div>

          {/* Right rail — embedded feed */}
          <div className="md:col-span-7 lg:col-span-8">
            <div
              data-testid="facebook-feed-embed"
              className="relative border border-[#E5E7EB] bg-[#F5F7FA] p-4 md:p-6"
            >
              {/* status bar */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E5E7EB]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#84FB00] animate-pulse" />
                  <span className="font-display tracking-[0.25em] uppercase text-[10px] text-[#4B5563]">
                    Live Feed · facebook.com
                  </span>
                </div>
                {!ready && !failed && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-display tracking-widest uppercase text-[#9CA3AF]">
                    <RefreshCw size={11} className="animate-spin" /> Loading
                  </span>
                )}
              </div>

              <div
                ref={containerRef}
                className="fb-feed-wrap relative overflow-hidden min-h-[500px] flex justify-center"
              >
                {/* Facebook Page Plugin — parsed by SDK on mount */}
                <div
                  className="fb-page"
                  data-href={facebookUrl}
                  data-tabs="timeline"
                  data-width="500"
                  data-height="620"
                  data-small-header="false"
                  data-adapt-container-width="true"
                  data-hide-cover="false"
                  data-show-facepile="true"
                >
                  <blockquote cite={facebookUrl} className="fb-xfbml-parse-ignore">
                    <a href={facebookUrl} target="_blank" rel="noreferrer noopener">
                      See our latest posts on Facebook
                    </a>
                  </blockquote>
                </div>

                {failed && (
                  <div
                    data-testid="facebook-feed-fallback"
                    className="absolute inset-0 flex flex-col items-center justify-center text-center bg-[#F5F7FA]/95 p-8"
                  >
                    <Facebook size={32} className="text-[#1877F2] mb-3" />
                    <p className="text-[#1F2937] max-w-sm">
                      Can't load the live feed here (usually a privacy blocker).
                      Tap below to see our latest work on Facebook.
                    </p>
                    <a
                      href={facebookUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="mt-5 inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0E63D4] px-5 py-3 font-display tracking-widest uppercase text-[#0A0E1A] text-sm"
                    >
                      <Facebook size={14} /> Open Facebook
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
